import { useEffect, useRef } from 'react'
import { useOidc } from '@/features/oidc/oidc'
import { openSession, removeSubscription } from '@braze/web-sdk'
import { useBrazeStore } from './braze.store'
import { StringValues } from './types'
import { useFeatureFlag } from '@/lib/use-feature-flag'
import { RegisteredRouter } from '@tanstack/react-router'
import { useBrazeContentCardsSubscription } from '@/features/braze/use-braze-content-cards-subscription'
import { useBrazeInAppMessagesSubscription } from '@/features/braze/use-braze-in-app-messages-subscription'

type UseBrazeSubscriptionsProps = {
  router: RegisteredRouter
}

export const useBrazeSubscriptions = <Extras extends StringValues<Extras>>({
  router,
}: UseBrazeSubscriptionsProps) => {
  const hasSessionOpened = useRef(false)
  const { isUserLoggedIn, decodedIdToken } = useOidc()
  const userId = decodedIdToken?.['pam-aid']

  const setBrazeState = useBrazeStore((state) => state.setBrazeState)

  const { enabled: isIppEnabled, isPending: isIppPending } = useFeatureFlag({
    flagKey: 'eclipse-release-in-product-popups',
  })

  const { handleContentCardUpdate, subscribeToContentCards } =
    useBrazeContentCardsSubscription()
  const { subscribeToInAppMessages } = useBrazeInAppMessagesSubscription({
    router,
  })

  useEffect(() => {
    let contentCardsSubGuid: string | undefined
    let inAppMessageSubGuid: string | undefined

    const setupBraze = () => {
      try {
        contentCardsSubGuid = subscribeToContentCards()
        inAppMessageSubGuid = subscribeToInAppMessages()

        openSession()

        handleContentCardUpdate()
      } catch (e) {
        console.error('Braze: A critical error occurred during setup.', e)
        setBrazeState({ isError: true })
      }
    }

    if (
      isUserLoggedIn &&
      userId &&
      !isIppPending &&
      !hasSessionOpened.current
    ) {
      hasSessionOpened.current = true
      setupBraze()
    }

    return () => {
      if (contentCardsSubGuid) {
        removeSubscription(contentCardsSubGuid)
      }
      if (inAppMessageSubGuid) {
        removeSubscription(inAppMessageSubGuid)
      }
    }
  }, [
    isUserLoggedIn,
    userId,
    isIppPending,
    isIppEnabled,
    setBrazeState,
    subscribeToContentCards,
    handleContentCardUpdate,
    subscribeToInAppMessages,
  ])
}
